import { isBefore } from 'date-fns'
import { some, get } from 'lodash'
import { IOrderItem, TOrderPlatform } from '../../model/order.store'
import { ICustomer } from '../../model/customer.model'

/**
 * Chương trình "Ưu đãi khách mới - đặt bánh có quà" (Savor Cake HN) — WT #106468.
 *
 * Khách mới (chưa từng mua bánh sinh nhật tại Savor) đặt bánh sinh nhật (trừ bánh
 * hộp thiếc) tại Hà Nội, từ 22/6/2026 → được tặng free 01 phụ kiện sinh nhật trong
 * danh sách whitelist (tính theo từng đơn, tối đa 1 quà/đơn).
 *
 * Luồng POS app.savor.vn tạo thẳng saleEntry, lấy giá computed sẵn từ client AS-IS
 * (không qua pipeline ERP) nên quà được tính client-side ở đây — mirror pattern quà
 * Bộ dao dĩa cao cấp. Eligibility "khách mới" lấy từ backend qua cờ
 * `customer.isNewSavorCakeGiftEligible` (endpoint value-vouchers, PR #6813).
 */

/** 7 mã phụ kiện được phép tặng (MKT cung cấp, chốt ở WT #106429) — kèm tên để hiện cho cashier. */
export const NEW_CUSTOMER_CAKE_GIFT_ITEMS = [
  { sku: 'V2616', name: 'Mũ sinh nhật giấy hình vương miện' },
  { sku: 'V1579', name: 'Que cắm Happy Anniversary' },
  { sku: 'V1426', name: 'Mũ sinh nhật' },
  { sku: 'V1278', name: 'Que cắm Happy birthday tròn vàng 16cm' },
  { sku: 'V9952', name: 'Nến sinh nhật xoắn dài màu sắc 17cm (hộp 6 chiếc)' },
  { sku: 'V1279', name: 'Nến sinh nhật nhũ vàng (10 chiếc)' },
  { sku: 'V9136', name: 'Kính Happy Birthday' }
]

export const NEW_CUSTOMER_CAKE_GIFT_SKUS = NEW_CUSTOMER_CAKE_GIFT_ITEMS.map(item => item.sku)

/** Tên chương trình — set thẳng bằng code (không qua SaleProgram module). */
export const NEW_CUSTOMER_CAKE_GIFT_PROGRAM = 'Ưu đãi khách mới - đặt bánh có quà'

/** Áp dụng từ 22/6/2026. */
const PROGRAM_START_DATE = new Date('2026-06-22T00:00:00+07:00')

/** Bánh hộp thiếc bị loại khỏi phạm vi bánh sinh nhật (đúng chữ MKT "trừ bánh hộp thiếc"). */
const EXCLUDED_BIRTHDAY_CAKE_SUBCAT = 'Bánh hộp thiếc'

/**
 * Đơn có ít nhất 1 bánh sinh nhật (cat 'Bánh sinh nhật') trừ bánh hộp thiếc.
 * Dùng `productCatWorkName` chứ KHÔNG dùng `category` vì cả 'Bánh sinh nhật' lẫn
 * 'Phụ kiện BSN' đều map về category 'birthday-cake' (xem product.store convertCategory) —
 * nếu xét theo category thì đơn chỉ có phụ kiện cũng bị tính nhầm là có bánh.
 * Giữ su kem/tart/whipping theo phạm vi chương trình.
 */
export function hasBirthdayCakeExceptTinBox(items: IOrderItem[]): boolean {
  return some(
    items,
    item =>
      item.productCatWorkName === 'Bánh sinh nhật' &&
      item.subCatWorkName !== EXCLUDED_BIRTHDAY_CAKE_SUBCAT
  )
}

/**
 * Đơn thuộc phạm vi chương trình (chưa xét khách mới hay chưa): HN + đơn trực tiếp
 * SAVOR_SHOP + có bánh sinh nhật (trừ hộp thiếc) + từ 22/6. Dùng để bắt buộc nhập SĐT.
 */
export function isInNewCustomerCakeGiftWindow({
  items,
  region,
  orderPlatform,
  now
}: {
  items: IOrderItem[]
  region: string
  orderPlatform: TOrderPlatform
  now: Date
}): boolean {
  if (isBefore(now, PROGRAM_START_DATE)) {
    return false
  }
  // Chỉ Hà Nội (region NORTH)
  if (region !== 'NORTH') {
    return false
  }
  // Chỉ đơn mua trực tiếp tại cửa hàng
  if (orderPlatform !== 'SAVOR_SHOP') {
    return false
  }
  return hasBirthdayCakeExceptTinBox(items)
}

/**
 * Đơn đủ điều kiện tặng quà: thuộc phạm vi chương trình + khách đã chọn là khách mới
 * (cờ `isNewSavorCakeGiftEligible` từ backend).
 */
export function checkNewCustomerCakeGiftEligibility({
  customer,
  items,
  region,
  orderPlatform,
  now
}: {
  customer: ICustomer | null
  items: IOrderItem[]
  region: string
  orderPlatform: TOrderPlatform
  now: Date
}): { isEligible: boolean } {
  if (!isInNewCustomerCakeGiftWindow({ items, region, orderPlatform, now })) {
    return { isEligible: false }
  }
  if (!get(customer, 'isNewSavorCakeGiftEligible', false)) {
    return { isEligible: false }
  }
  return { isEligible: true }
}
